import { useEffect, useMemo, useState } from "react";
import { DESIGN_SCHEDULES, PUBLICATION_LEVELS, buildSchedule, schedulesForLevel } from "../../config/designSchedules";
import { collectQualifications } from "../../config/scheduleQualifications";
import { parsePublication, isSchedulePublished, isQualificationPublished, setSchedulePublished, setQualificationPublished, setPublicationLevel, samePublication } from "../../utils/schedulePublication";
import DesignScheduleTable from "../../components/DesignScheduleTable";

// Design Schedules (P1) — generated, read-only views of this revision's
// canonical design data at a chosen publication level, with a print layout.
// Lives inside Takeoff & Design because every canonical input it needs is
// already loaded there; the data contract is props only, so the same view
// can move to a proposal/export surface later without changing the schedules.
//
// Phase E: the level, the included schedules and the printed qualifications
// are the revision's PUBLICATION configuration (`publication` = the stored
// JSON, EstimateRevisions.SchedulePublicationJson; `onSavePublication(json)`
// persists it). Qualifications (scheduleQualifications.js) print ONCE per
// publication, deduplicated across the schedules that reference them.

const PRINT_CSS = `
@media print {
  body * { visibility: hidden !important; }
  .design-schedules-print, .design-schedules-print * { visibility: visible !important; }
  .design-schedules-print { position: absolute; left: 0; top: 0; width: 100%; padding: 0 !important; }
  .design-schedules-print .design-schedule { page-break-inside: avoid; }
  .design-schedules-print table { font-size: 10px; }
  .design-schedules-print th, .design-schedules-print td { padding: 3px 5px !important; }
}
`;

const pill = (on) => ({
  padding: "4px 12px", fontSize: 12, fontWeight: 700, cursor: "pointer", borderRadius: 999,
  background: on ? "var(--cs-blue)" : "var(--surface)", color: on ? "#fff" : "var(--text2)",
  border: `1px solid ${on ? "var(--cs-blue)" : "var(--cs-border)"}`,
});

export default function DesignSchedulesView({ ctx, header, projectName, publication = null, onSavePublication = null }) {
  const saved = useMemo(() => parsePublication(publication), [publication]);
  const [pub, setPub] = useState(saved);
  useEffect(() => { setPub(saved); }, [saved]);
  const dirty = !samePublication(pub, saved);
  const level = pub.level;
  const available = schedulesForLevel(level);
  const shown = available.filter(s => isSchedulePublished(pub, s.key));
  const built = useMemo(() => shown.map(def => buildSchedule(def, ctx, level)), [shown, ctx, level]);
  const qualifications = useMemo(() => collectQualifications(built), [built]);
  const printedQualifications = qualifications.filter(q => isQualificationPublished(pub, q.key));
  const levelInfo = PUBLICATION_LEVELS.find(l => l.key === level);
  const canSave = typeof onSavePublication === "function";

  return (
    <div>
      <style>{PRINT_CSS}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Publish as</span>
        {PUBLICATION_LEVELS.map(l => (
          <button key={l.key} onClick={() => setPub(p => setPublicationLevel(p, l.key))} style={pill(level === l.key)} title={l.description}>{l.label}</button>
        ))}
        <span style={{ fontSize: 11.5, color: "var(--text3)" }}>{levelInfo?.description}</span>
        <span style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          {canSave && (
            <button onClick={() => onSavePublication(pub)} disabled={!dirty} style={{ ...pill(dirty), opacity: dirty ? 1 : 0.6, cursor: dirty ? "pointer" : "default" }}
              title="Store this level, schedule selection and qualification list as the revision's publication configuration (carried to the next revision)">
              {dirty ? "💾 Save as revision publication" : "✓ Saved publication"}
            </button>
          )}
          {!canSave && publication && <span style={{ fontSize: 11.5, color: "var(--text3)" }} title="This revision is locked — its publication configuration is frozen">🔒 saved publication</span>}
          <button onClick={() => window.print()} style={pill(false)} title="Print or save the visible schedules as PDF (browser print dialog)">🖨 Print / Save as PDF</button>
        </span>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12, fontSize: 12 }}>
        <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Schedules</span>
        {DESIGN_SCHEDULES.map(s => {
          const publishable = available.includes(s);
          return (
            <label key={s.key} style={{ display: "flex", alignItems: "center", gap: 5, opacity: publishable ? 1 : 0.5 }} title={publishable ? "" : `Not published at the ${levelInfo?.label} level`}>
              <input type="checkbox" disabled={!publishable || !canSave && !!publication} checked={publishable && isSchedulePublished(pub, s.key)}
                onChange={e => setPub(p => setSchedulePublished(p, s.key, e.target.checked))} />
              {s.title}
            </label>
          );
        })}
      </div>
      {qualifications.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 12, fontSize: 12 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Qualifications</span>
          {qualifications.map(q => (
            <label key={q.key} style={{ display: "flex", alignItems: "center", gap: 5 }} title={`${q.type} — ${q.text}\nReferenced by: ${q.schedules.join(", ")}`}>
              <input type="checkbox" disabled={!canSave && !!publication} checked={isQualificationPublished(pub, q.key)}
                onChange={e => setPub(p => setQualificationPublished(p, q.key, e.target.checked))} />
              {q.title}
            </label>
          ))}
        </div>
      )}
      <div style={{ fontSize: 11.5, color: "var(--text3)", marginBottom: 12 }}>
        Generated from this revision's rooms, criteria, Basis of Design selections, equipment, panels and engine results — edit those, not the schedule.
        Qualifications print once, below the schedules, for every schedule that carries them.
      </div>

      <div className="design-schedules-print">
        <div className="design-schedules-print__title" style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{projectName || "Estimate"} — Design Schedules</div>
          <div style={{ fontSize: 11, color: "var(--text3)" }}>
            {header?.RevisionLabel ? `Revision ${header.RevisionNumber ?? ""} — ${header.RevisionLabel}` : header?.RevisionNumber != null ? `Revision ${header.RevisionNumber}` : ""}
            {" · "}{levelInfo?.label} · generated {new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
          </div>
        </div>
        {built.map(s => <DesignScheduleTable key={s.key} schedule={s} />)}
        {!built.length && <div style={{ fontSize: 12, color: "var(--text3)", fontStyle: "italic" }}>No schedules selected.</div>}
        {printedQualifications.length > 0 && (
          <section className="design-schedule design-schedules-qualifications" aria-labelledby="design-schedules-qualifications-title" style={{ marginTop: 18 }}>
            <h3 id="design-schedules-qualifications-title" style={{ fontSize: 13, fontWeight: 800, margin: "0 0 6px" }}>Qualifications</h3>
            <ol style={{ margin: 0, paddingLeft: 18, fontSize: 11, color: "var(--text2)" }}>
              {printedQualifications.map(q => <li key={q.key} data-qualification={q.key}>{q.text}</li>)}
            </ol>
          </section>
        )}
      </div>
    </div>
  );
}
